import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Share2, CirclePlus } from "lucide-react";
import { localizeReference } from "@/lib/bibleBooks";
import { toast } from "sonner";

export const VERSE_BGS = [
  { key: "sunrise", src: "/verse-bg/sunrise.jpg" },
  { key: "dove", src: "/verse-bg/dove.jpg" },
  { key: "stars", src: "/verse-bg/stars.jpg" },
  { key: "wheat", src: "/verse-bg/wheat.jpg" },
];

export const VerseWidget = () => {
  const { lang, t, user } = useApp();
  const [verse, setVerse] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [bg, setBg] = useState(null); // null = classic gradient | "/verse-bg/*.jpg"
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchVerse = () => api.get("/verses/today").then(({ data }) => { if (!cancelled) setVerse(data.verse); }).catch(() => {}).finally(() => { if (!cancelled) setLoaded(true); });
    const cancelFirst = whenFeedReady(fetchVerse);
    // Refresh when the day's verse gets published (tab refocus + periodic check).
    const onFocus = () => { if (document.visibilityState === "visible") fetchVerse(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    const iv = setInterval(fetchVerse, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      cancelFirst();
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      clearInterval(iv);
    };
  }, []);

  if (!verse) {
    if (!loaded) return (
      <div className="rounded-2xl p-6 relative overflow-hidden animate-pulse" data-testid="verse-skeleton" style={{ background: "var(--gradient-navy)" }}>
        <div style={{ width: "40%", height: 11, borderRadius: 6, background: "rgba(255,255,255,0.25)" }} />
        <div className="space-y-2 mt-4">
          <div style={{ width: "100%", height: 14, borderRadius: 6, background: "rgba(255,255,255,0.18)" }} />
          <div style={{ width: "92%", height: 14, borderRadius: 6, background: "rgba(255,255,255,0.18)" }} />
          <div style={{ width: "60%", height: 14, borderRadius: 6, background: "rgba(255,255,255,0.18)" }} />
        </div>
        <div style={{ width: "35%", height: 12, borderRadius: 6, background: "rgba(255,255,255,0.25)", marginTop: 18 }} />
      </div>
    );
    return null;
  }
  const text = verse[`text_${lang}`] || verse.text_fr;
  const ref = localizeReference(verse.reference, lang);

  const shareFeed = async () => {
    setSharing(true);
    try {
      await api.post("/posts", { content: "", verse: { text, reference: verse.reference, bg } });
      toast.success(t("verse.shared", "Verset partagé sur ton fil ✨"));
      window.dispatchEvent(new CustomEvent("yalola:post_created"));
      setOpen(false);
    } catch { toast.error(t("verse.share_error", "Erreur lors du partage")); }
    finally { setSharing(false); }
  };

  const shareStory = async () => {
    setSharing(true);
    try {
      await api.post("/stories", {
        media_type: "text",
        text: `« ${text} »\n— ${ref}`,
        bg: bg || "navy",
      });
      toast.success(t("verse.shared_story", "Verset partagé en story ✨"));
      window.dispatchEvent(new CustomEvent("yalola:story_created"));
      setOpen(false);
    } catch { toast.error(t("verse.share_error", "Erreur lors du partage")); }
    finally { setSharing(false); }
  };

  return (
    <div
      className="rounded-2xl p-6 relative overflow-hidden"
      data-testid="verse-of-day-widget"
      style={{ background: "var(--gradient-navy)", boxShadow: "0 12px 30px -14px rgba(45, 90, 76, 0.5)" }}
    >
      <div className="absolute -top-8 -right-8 opacity-[0.12]" aria-hidden>
        <Sparkles size={120} className="text-white" />
      </div>
      <div className="absolute -bottom-10 -left-6 opacity-[0.08]" aria-hidden>
        <Sparkles size={90} className="text-white" />
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-accent)" }}>
          <Sparkles size={14} /> {t("verse.title", "Verset du jour")}
        </div>
        <p className="serif-italic mt-3 text-lg leading-relaxed text-white/95">
          « {text} »
        </p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="text-sm font-semibold" style={{ color: "var(--yalola-accent)" }}>
            — {ref}
          </div>
          {user && (
            <button
              onClick={() => setOpen(true)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 transition-colors hover:bg-white/20"
              style={{ background: "rgba(255,255,255,0.12)", color: "white" }}
              data-testid="verse-share-btn"
            >
              <Share2 size={12} /> {t("verse.share", "Partager")}
            </button>
          )}
        </div>
      </div>

      {/* Background chooser dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!sharing) setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="verse-bg-dialog">
          <DialogHeader>
            <DialogTitle className="text-center font-display">{t("verse.share_title", "Partager le verset")}</DialogTitle>
            <DialogDescription className="sr-only">{t("verse.choose_bg", "Choisir un fond")}</DialogDescription>
          </DialogHeader>

          {/* Live preview */}
          <div
            className="rounded-2xl p-6 relative overflow-hidden"
            style={bg
              ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
              : { background: "var(--gradient-navy)" }}
            data-testid="verse-share-preview"
          >
            {bg && <div className="absolute inset-0 bg-black/35" aria-hidden />}
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-accent)" }}>{t("verse.title", "Verset du jour")}</div>
              <p className="serif-italic mt-2 text-base leading-relaxed text-white/95" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>« {text} »</p>
              <div className="mt-3 text-sm font-semibold" style={{ color: "var(--yalola-accent)" }}>— {ref}</div>
            </div>
          </div>

          {/* Background options */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: "var(--yalola-text-muted)" }}>{t("verse.choose_bg", "Choisir un fond")}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setBg(null)}
                className="h-12 w-16 rounded-lg overflow-hidden grid place-items-center text-[9px] font-bold text-white uppercase"
                style={{ background: "var(--gradient-navy)", boxShadow: !bg ? "0 0 0 2px white, 0 0 0 4px var(--yalola-primary)" : "none" }}
                data-testid="verse-bg-default"
              >
                {t("verse.bg_default", "Classique")}
              </button>
              {VERSE_BGS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setBg(b.src)}
                  className="h-12 w-16 rounded-lg overflow-hidden transition-transform hover:scale-105"
                  style={{ boxShadow: bg === b.src ? "0 0 0 2px white, 0 0 0 4px var(--yalola-primary)" : "none" }}
                  data-testid={`verse-bg-${b.key}`}
                >
                  <img src={b.src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-xl h-11" onClick={shareStory} disabled={sharing} data-testid="verse-share-story-btn">
              <CirclePlus size={15} className="mr-1.5" /> {t("verse.share_story", "Partager en story")}
            </Button>
            <Button className="flex-1 pill-primary rounded-xl h-11" onClick={shareFeed} disabled={sharing} data-testid="verse-share-confirm">
              {sharing ? "..." : t("verse.share_feed", "Partager dans le fil")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
